import { FILTER_TYPE, PER_PAGE } from "@/constant/constants";
import { useCallback, useMemo, useRef } from "react";

import Actions from "./Actions";
import { CONFIRMATION_MODAL_TYPES } from "@/Modals/NewConfirmationModal/constants";
import DataTable from "@/components/NewTable";
import { statusFilters } from "@/constant/statusConstants";
import { useGet } from "@/apiHooks/HttpFunctions";
import { useModalConfirmation } from "@/context/ModalConfirmationContext";
import useUrlParams from "@/hooks/useUrlParams";
import BaseImportModal from "@/Modals/Profiles/BaseImport/BaseImportModal";

export default function BaseTable({
  label,
  apiURL,
  columns = [],
  ModalComponent,
  importData,
  enableStatus = true,
  statusColumn = null,
  enableExport = true,
  enableActions = true,
  enableAdd = true,
  customEditFn = null,
  enableDelete = true,
  enableSearch = true,
  enableFilter = true,
  additionalFilters = [],
  customHeaderActionsData = [],
  confirmationModalTitleKey = null,
  isFullPageTable = true,
  enablePagination = true,
  enableSortData = false,
}) {
  // Hooks
  const params = useUrlParams();
  const { openModalConfirmation } = useModalConfirmation();

  // Refs
  const modalRef = useRef(null);
  const modalImportRef = useRef(null);

  // Derived States
  const filterData = useMemo(() => {
    const statusFiltersMap = statusFilters();
    return [
      enableStatus && {
        id: "filter[status]",
        label: "Status",
        type: FILTER_TYPE.SELECT,
        options: Object.entries(statusFiltersMap).map(([value, label]) => ({
          label,
          value,
        })),
      },
      ...additionalFilters,
    ].filter(Boolean);
  }, [enableStatus, additionalFilters]);

  // Callbacks
  const handleModalAction = useCallback(
    (type, data) => {
      openModalConfirmation({
        type,
        title: confirmationModalTitleKey
          ? confirmationModalTitleKey(data)
          : data.name ?? "",
        id: data?.id,
        apiURL,
      });
    },
    [openModalConfirmation, confirmationModalTitleKey, apiURL]
  );

  const handleOnClickAdd = useCallback(() => modalRef.current?.open(), []);
  const handleOnClickImport = useCallback(
    () => modalImportRef.current?.open(),
    []
  );
  const handleOnClickEdit = useCallback(
    (data) => modalRef.current?.open({ id: data.id }),
    []
  );

  // Data Fetching
  const { data, isFetching, error, refetch } = useGet({
    apiURL,
    params: {
      ...params,
      perPage: params.perPage || PER_PAGE,
    },
  });

  // Merged Columns
  const mergedColumns = useMemo(() => {
    const actionsColumn = {
      accessorKey: "actions",
      header: "Actions",
      enableResizing: false,
      enableSorting: false,
      enablePinning: true,
      cell: ({ row }) => (
        <Actions
          row={row}
          handleOnClickDelete={(data) =>
            handleModalAction(CONFIRMATION_MODAL_TYPES.DELETE, data)
          }
          handleOnChangeStatus={(data) =>
            handleModalAction(CONFIRMATION_MODAL_TYPES.CHANGE_STATUS, data)
          }
          handleOnClickEdit={
            customEditFn ? () => customEditFn(row) : handleOnClickEdit
          }
          enableStatus={enableStatus}
          enableDelete={enableDelete}
          statusColumn={statusColumn}
        />
      ),
    };

    return [enableActions && actionsColumn, ...columns].filter(Boolean);
  }, [
    enableActions,
    columns,
    customEditFn,
    handleOnClickEdit,
    enableStatus,
    enableDelete,
    statusColumn,
    handleModalAction,
  ]);

  // Render
  if (error) {
    return (
      <div>
        <p>Error loading data...</p>
        <button onClick={refetch}>Retry</button>
      </div>
    );
  }

  return (
    <>
      <DataTable
        data={data}
        columns={mergedColumns}
        isLoading={isFetching}
        searchKey={enableSearch && "filter[keyword]"}
        exportData={
          enableExport && {
            api: apiURL,
            label: `${label} List`,
          }
        }
        importData={
          importData && {
            onClick: handleOnClickImport,
          }
        }
        addData={
          enableAdd && {
            label: `Add ${label}`,
            onClick: handleOnClickAdd,
          }
        }
        sortData={
          enableSortData && {
            api: apiURL,
          }
        }
        customHeaderActionsData={customHeaderActionsData}
        filterData={enableFilter && filterData}
        isFullPageTable={isFullPageTable}
        enablePagination={enablePagination}
      />
      {ModalComponent && <ModalComponent ref={modalRef} />}
      {importData && (
        <BaseImportModal
          ref={modalImportRef}
          importData={importData}
          apiURL={apiURL}
        />
      )}
    </>
  );
}
